import React, { useState } from 'react';

// ─── Pending Vendor Card (Admin) ──────────────────────────────────────────────
function PendingVendorCard({ v, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const m = v.metadata || {};
  const cats = m.allCategories || [v.category];
  const photos = m.photoUrls || [];
  const coiUrl = m.coiUrl || null;
  const lookbookUrl = m.lookbookUrl || null;
  const submitted = v.created_at ? new Date(v.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

  const Field = ({label, val}) => val ? (
    <div style={{marginBottom:10}}>
      <div style={{fontSize:11,fontWeight:700,color:'#a89a8a',textTransform:'uppercase',letterSpacing:0.5,marginBottom:2}}>{label}</div>
      <div style={{fontSize:14,color:'#1a1410'}}>{val}</div>
    </div>
  ) : null;

  return (
    <div style={{background:'#fff',border:'1.5px solid #e8ddd0',borderRadius:12,overflow:'hidden'}}>
      {/* Summary row */}
      <div style={{padding:'16px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div style={{display:'flex',alignItems:'center',gap:14,flex:1,minWidth:200}}>
          <div style={{fontSize:28}}>{v.emoji||'🏪'}</div>
          <div>
            <div style={{fontWeight:700,fontSize:15,color:'#1a1410'}}>{v.name}</div>
            <div style={{fontSize:12,color:'#7a6a5a'}}>{v.contact_name||'—'} · {v.contact_email||'—'} · {v.home_zip} · {v.radius}mi</div>
            <div style={{fontSize:12,color:'#a89a8a',marginTop:2}}>{cats.join(', ')} · Submitted {submitted}</div>
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <button onClick={()=>setExpanded(e=>!e)}
            style={{background:'#f5f0ea',color:'#1a1410',border:'1px solid #e0d5c5',borderRadius:6,padding:'7px 14px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
            {expanded ? '▾ Collapse' : '▸ View Full Application'}
          </button>
          <button onClick={onApprove}
            style={{background:'#2e7d32',color:'#fff',border:'none',borderRadius:6,padding:'7px 14px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
            ✓ Approve
          </button>
          <button onClick={onReject}
            style={{background:'#c62828',color:'#fff',border:'none',borderRadius:6,padding:'7px 14px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
            ✗ Reject
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{borderTop:'1px solid #e8ddd0',padding:'20px 24px',background:'#faf8f5'}}>
          <div className="modal-3col" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 32px'}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:12,borderBottom:'1px solid #e8ddd0',paddingBottom:6}}>Contact & Business</div>
              <Field label="Business Name" val={v.name} />
              <Field label="Owner" val={v.contact_name} />
              <Field label="Email" val={v.contact_email} />
              <Field label="Phone" val={v.contact_phone} />
              <Field label="Years Active" val={m.yearsActive} />
              {/* Clickable links for verification */}
              <div style={{marginTop:10,borderTop:'1px solid #e8ddd0',paddingTop:10}}>
                <div style={{fontSize:11,fontWeight:700,color:'#a89a8a',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Links (click to verify)</div>
                {[
                  {label:'🌐 Website', url:v.website},
                  {label:'📸 Instagram', url:v.instagram},
                  {label:'👤 Facebook', url:m.facebook},
                  {label:'🎵 TikTok', url:m.tiktok},
                  {label:'▶️ YouTube', url:m.youtube},
                  {label:'🔗 Other', url:m.otherSocial},
                ].filter(l=>l.url).map(l=>(
                  <div key={l.label} style={{marginBottom:6}}>
                    <a href={l.url.startsWith('http')?l.url:'https://'+l.url} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:13,color:'#1a4a6b',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4,wordBreak:'break-all'}}>
                      {l.label}: {l.url}
                    </a>
                  </div>
                ))}
                {![v.website,v.instagram,m.facebook,m.tiktok,m.youtube,m.otherSocial].some(Boolean) && (
                  <div style={{fontSize:12,color:'#a89a8a',fontStyle:'italic'}}>No links provided</div>
                )}
              </div>
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:12,borderBottom:'1px solid #e8ddd0',paddingBottom:6}}>Categories & Details</div>
              <Field label="Categories" val={cats.join(', ')} />
              <Field label="Subcategories" val={(v.subcategories||[]).length>0 ? v.subcategories.join(', ') : null} />
              <Field label="Event Types" val={(v.tags||[]).length>0 ? v.tags.join(', ') : null} />
              <Field label="Description" val={v.description} />
              <Field label="Home Zip" val={v.home_zip} />
              <Field label="Travel Radius" val={`${v.radius} miles`} />
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:12,borderBottom:'1px solid #e8ddd0',paddingBottom:6}}>Booth & Logistics</div>
              <Field label="Insurance" val={v.insurance ? '✓ Yes — has COI' : '✗ No'} />
              <Field label="Minimum Purchase" val={v.has_min_purchase ? `Yes — $${v.min_purchase_amt} minimum` : 'No minimum'} />
              {/* Private event fee hidden */}
              <Field label="Setup Time" val={m.setupTime ? `${m.setupTime} min` : null} />
              <Field label="Table Size" val={m.tableSize} />
              <Field label="Needs Electric" val={m.needsElectric ? 'Yes' : 'No'} />
              <Field label="Response Time" val={m.responseTime} />
              <Field label="Booking Lead Time" val={m.bookingLeadTime} />
              <Field label="Event Frequency" val={m.eventFrequency} />
            </div>
          </div>

          {/* Service Provider details */}
          {(m.isServiceProvider || m.vendorType?.service) && (
            <div style={{marginTop:16,borderTop:'1px solid #e8ddd0',paddingTop:16}}>
              <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:12}}>Service Provider Details</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 32px'}}>
                <Field label="Vendor Type" val={m.vendorType?.market && m.vendorType?.service ? 'Market Vendor + Service Provider' : m.vendorType?.service ? 'Service Provider Only' : 'Market Vendor'} />
                <Field label="Service Type" val={m.serviceType} />
                <Field label="Rate" val={m.serviceRateType==='quote' ? 'Quote based' : m.serviceRateType==='range' ? `${m.serviceRateMin} – ${m.serviceRateMax}` : m.serviceRateMin} />
                <Field label="Min Duration" val={m.minBookingDuration} />
                <Field label="Service Categories" val={(m.serviceCategories||[]).join(', ') || null} />
                <Field label="Service Subcategories" val={(m.serviceSubcategories||[]).join(', ') || null} />
              </div>
              {m.serviceDescription && <Field label="Service Description" val={m.serviceDescription} />}
            </div>
          )}

          {/* Uploaded files */}
          <div style={{marginTop:20,borderTop:'1px solid #e8ddd0',paddingTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:'#1a1410',marginBottom:12}}>Uploaded Files & Attachments</div>
            {photos.length === 0 && !coiUrl && !lookbookUrl && (
              <div style={{fontSize:13,color:'#a89a8a',fontStyle:'italic',marginBottom:12}}>No files uploaded with this application.</div>
            )}
          {(photos.length > 0 || coiUrl || lookbookUrl) && (
            <div>
              {photos.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#a89a8a',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Business Photos ({photos.length})</div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    {photos.map((url,i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{display:'block'}}>
                        <img src={url} alt={`Photo ${i+1}`} style={{width:180,height:180,objectFit:'cover',borderRadius:8,border:'1px solid #e0d5c5',cursor:'pointer'}} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                {coiUrl && (
                  <a href={coiUrl} target="_blank" rel="noopener noreferrer"
                    style={{display:'flex',alignItems:'center',gap:8,background:'#d4f4e0',border:'1px solid #b8e8c8',borderRadius:8,padding:'10px 16px',fontSize:13,color:'#1a6b3a',fontWeight:600,textDecoration:'none'}}>
                    📄 Certificate of Insurance
                  </a>
                )}
                {lookbookUrl && (
                  <a href={lookbookUrl} target="_blank" rel="noopener noreferrer"
                    style={{display:'flex',alignItems:'center',gap:8,background:'#e8f4fd',border:'1px solid #b8d8f0',borderRadius:8,padding:'10px 16px',fontSize:13,color:'#1a4a6b',fontWeight:600,textDecoration:'none'}}>
                    📋 Price Menu / Lookbook
                  </a>
                )}
              </div>
            </div>
          )}
          </div>

          {/* Bottom action bar */}
          <div style={{marginTop:20,paddingTop:16,borderTop:'1px solid #e8ddd0',display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button onClick={onApprove}
              style={{background:'#2e7d32',color:'#fff',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
              ✓ Approve Vendor
            </button>
            <button onClick={onReject}
              style={{background:'#c62828',color:'#fff',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
              ✗ Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingVendorCard;
